import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, CheckCheck, ChevronLeft } from "lucide-react";
import api from "@/lib/api";
import { timeAgo } from "@/lib/timeago";
import { Button } from "@/components/ui/button";

export default function Notifications() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadOnly, setUnreadOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/notifications?limit=200&unread_only=${unreadOnly}`);
      setRows(data.rows || []);
    } finally {
      setLoading(false);
    }
  }, [unreadOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const markOne = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };

  return (
    <div data-testid="notifications-page" className="mx-auto w-full max-w-3xl">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link
            to="/"
            className="mb-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
          >
            <ChevronLeft className="h-3 w-3" /> Dashboard
          </Link>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900">
            Notifications
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setUnreadOnly((v) => !v)}
            data-testid="notifications-toggle-unread"
          >
            {unreadOnly ? "Show all" : "Unread only"}
          </Button>
          <Button
            size="sm"
            onClick={markAll}
            className="gap-1.5 bg-cargodash-primary-600 hover:bg-cargodash-primary-700"
            data-testid="notifications-page-mark-all"
          >
            <CheckCheck className="h-4 w-4" /> Mark all read
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-10 text-center text-slate-500">
            <Inbox className="h-6 w-6" />
            <div className="text-sm">You're all caught up.</div>
          </div>
        ) : (
          rows.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => {
                if (!n.read) markOne(n.id);
                if (n.link) window.location.assign(n.link);
              }}
              data-testid={`notifications-page-item-${n.id}`}
              className={`flex w-full items-start gap-3 border-b border-slate-100 px-5 py-4 text-left transition hover:bg-slate-50 last:border-b-0 ${
                n.read ? "opacity-70" : ""
              }`}
            >
              <span
                className={`mt-1 h-2 w-2 shrink-0 rounded-full ${
                  n.read ? "bg-slate-200" : "bg-cargodash-primary-600"
                }`}
              />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-900">{n.title}</div>
                {n.body && <div className="mt-0.5 text-sm text-slate-500">{n.body}</div>}
                <div className="mt-1 text-[11px] text-slate-400">{timeAgo(n.ts)}</div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
